// 고객 여정 스텝퍼 — 고객·견적·계약·현장 상태로 문의→완료 여덟 단계를 판정한다(화면은 그리기만, 현장마다 한 벌).

import { formatDate } from "../../lib/format";
import type { Contract } from "../contract/rows";
import { CONTRACT_STATUS_LABEL } from "../contract/types";
import type { Customer } from "./types";
import { dDayLabel, dDayOf } from "../project/view";
import { PROJECT_STATUS_LABEL, type Project, type ProjectPhaseStatus, type ProjectStatus } from "../project/types";
import { QUOTE_KIND_LABEL, QUOTE_STATUS_LABEL, type Quote } from "../quote/types";
import { SIGNED_CONTRACT_STATUSES } from "./stage-events";

export const JOURNEY_STEPS = ["INQUIRY", "ROUGH", "DETAIL", "CONTRACT", "SCHEDULED", "IN_PROGRESS", "WARRANTY", "CLOSED"] as const;
export type JourneyStepKey = (typeof JOURNEY_STEPS)[number];

export const JOURNEY_STEP_LABEL: Record<JourneyStepKey, string> = {
  INQUIRY: "문의",
  ROUGH: QUOTE_KIND_LABEL.ROUGH,
  DETAIL: QUOTE_KIND_LABEL.DETAIL,
  CONTRACT: "계약",
  SCHEDULED: PROJECT_STATUS_LABEL.SCHEDULED,
  IN_PROGRESS: PROJECT_STATUS_LABEL.IN_PROGRESS,
  WARRANTY: PROJECT_STATUS_LABEL.WARRANTY,
  CLOSED: PROJECT_STATUS_LABEL.CLOSED,
};

// 선택 단계 — 거치지 않고 지나가면 「건너뜀」.
export const OPTIONAL_JOURNEY_STEPS: readonly JourneyStepKey[] = ["ROUGH", "WARRANTY"];

export type JourneyStepState = "done" | "current" | "todo" | "skipped" | "lost";

export interface JourneyStep {
  key: JourneyStepKey;
  label: string;
  optional: boolean;
  state: JourneyStepState;
  when: string | null;
  doc: { label: string; href: string } | null;
}

export type JourneyCustomer = Pick<Customer, "stage" | "created_at">;
export type JourneyQuote = Pick<Quote, "id" | "quote_no" | "kind" | "version" | "status" | "created_at" | "sent_at" | "decided_at">;
export type JourneyContract = Pick<Contract, "id" | "quote_id" | "contract_no" | "status" | "created_at" | "signed_at" | "terminated_at">;
export type JourneyProject = Pick<
  Project,
  | "id"
  | "contract_id"
  | "status"
  | "created_at"
  | "planned_start_date"
  | "planned_end_date"
  | "started_at"
  | "completion_date"
  | "warranty_end_date"
  | "closed_at"
>;

export interface JourneyInput {
  customer: JourneyCustomer;
  quotes: readonly JourneyQuote[];
  contracts: readonly JourneyContract[];
  // null 이면 현장 없는 고객의 여정(가장 최근 상세견적 계열 기준).
  project: JourneyProject | null;
  // 마지막으로 이탈로 옮긴 단계 이력의 시각(없으면 null) — 수동 이탈 뒤 재시작 판정에 쓴다.
  lastLostAt: string | null;
  today: string;
}

const DAY_MS = 86_400_000;
const dayOf = (value: string | null | undefined): string | null => (value ? formatDate(value, "") || null : null);
const daysBetween = (from: string, to: string): number =>
  Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / DAY_MS);
const byCreated = <T extends { created_at: string; id: string }>(a: T, b: T): number =>
  a.created_at.localeCompare(b.created_at) || a.id.localeCompare(b.id);
const quotePath = (id: string): string => `/app/quotes/${id}`;

export interface JourneySeries {
  quote_no: string;
  start: string;
  latest: JourneyQuote;
  quotes: JourneyQuote[];
}

// 상세견적 계열(quote_no) — 첫 버전을 만든 순서.
const detailSeries = (quotes: readonly JourneyQuote[]): JourneySeries[] => {
  const groups = new Map<string, JourneyQuote[]>();
  for (const q of quotes) {
    if (q.kind !== "DETAIL") continue;
    groups.set(q.quote_no, [...(groups.get(q.quote_no) ?? []), q]);
  }
  return [...groups.entries()]
    .map(([quote_no, list]) => {
      const sorted = [...list].sort((a, b) => a.version - b.version || byCreated(a, b));
      return { quote_no, start: [...list].sort(byCreated)[0].created_at, latest: sorted[sorted.length - 1], quotes: sorted };
    })
    .sort((a, b) => a.start.localeCompare(b.start) || a.quote_no.localeCompare(b.quote_no));
};

// 가견적은 상세견적과 이어지지 않으므로 — 앞 계열 시작 뒤부터 이 계열 시작까지 만든 가견적을 이 계열 것으로 본다(계열이 없으면 마지막 계열 뒤).
const roughsFor = (quotes: readonly JourneyQuote[], all: readonly JourneySeries[], series: JourneySeries | null): JourneyQuote[] => {
  const at = series ? all.indexOf(series) : all.length;
  const after = at > 0 ? all[at - 1].start : null;
  const until = series ? series.start : null;
  return quotes
    .filter((q) => q.kind === "ROUGH" && (after === null || q.created_at > after) && (until === null || q.created_at <= until))
    .sort(byCreated);
};

// 계열의 계약 — 파기(VOID)는 새 계약으로 대체되므로 빼고 가장 최근 것.
const contractOf = (contracts: readonly JourneyContract[], series: JourneySeries | null): JourneyContract | null => {
  if (!series) return null;
  const ids = new Set(series.quotes.map((q) => q.id));
  return [...contracts].filter((c) => ids.has(c.quote_id) && c.status !== "VOID").sort(byCreated).pop() ?? null;
};

const PROJECT_POSITION: Record<ProjectStatus, number> = { SCHEDULED: 4, IN_PROGRESS: 5, WARRANTY: 6, CLOSED: 7 };

export interface JourneyContext {
  series: JourneySeries | null;
  // 이 여정의 계약 — 현장이 있으면 그 현장의 계약, 없으면 계열의 살아 있는 계약.
  live: JourneyContract | null;
  rough: JourneyQuote | null;
  signed: boolean;
  // 지금 단계의 자리(0~7) — 7(완료)이면 모든 단계가 지나간 것이다.
  pos: number;
  lost: boolean;
}

// 이탈 시각들 — 이탈한 견적의 결정일 · 해지한 계약의 해지일 · 마지막 이탈 이력(오래된 순).
const lossTimesOf = (
  quotes: readonly Pick<JourneyQuote, "status" | "created_at" | "decided_at">[],
  contracts: readonly Pick<JourneyContract, "status" | "created_at" | "terminated_at">[],
  lastLostAt: string | null
): string[] =>
  [
    ...quotes.filter((q) => q.status === "LOST").map((q) => q.decided_at ?? q.created_at),
    ...contracts.filter((c) => c.status === "TERMINATED").map((c) => c.terminated_at ?? c.created_at),
    ...(lastLostAt ? [lastLostAt] : []),
  ].sort((a, b) => Date.parse(a) - Date.parse(b));

const isAfter = (at: string, since: string): boolean => Date.parse(at) > Date.parse(since);
const latestOf = (times: readonly string[]): string | null => times.reduce<string | null>((a, b) => (a === null || isAfter(b, a) ? b : a), null);

// 견적의 마지막 활동 — 만든 때와 발송한 때 중 늦은 쪽(재시작 판정과 판 선택이 같은 기준).
export const quoteActivityAt = (q: Pick<JourneyQuote, "created_at" | "sent_at">): string =>
  q.sent_at && isAfter(q.sent_at, q.created_at) ? q.sent_at : q.created_at;

// 판 선택도 재시작 판정과 같은 기준(견적의 마지막 활동) — 계열은 그 견적들 중 가장 늦은 활동.
const byActivity = (a: string, b: string): number => Date.parse(a) - Date.parse(b);
const seriesActivityAt = (s: JourneySeries): string => latestOf(s.quotes.map(quoteActivityAt)) ?? s.start;
const byQuoteActivity = (a: JourneyQuote, b: JourneyQuote): number => byActivity(quoteActivityAt(a), quoteActivityAt(b)) || byCreated(a, b);

// 현장 없는 여정이 볼 계열 — 가장 최근에 손댄 상세견적 계열.
const latestSeries = (all: readonly JourneySeries[]): JourneySeries | null =>
  [...all].sort((a, b) => byActivity(seriesActivityAt(b), seriesActivityAt(a)) || byCreated(b.latest, a.latest))[0] ?? null;

// 살아 있는 계약(파기·해지 아님)이 붙은 계열 — 이탈 시각과 상관없이 판에서 빼지 않는다.
const hasLiveContract = (contracts: readonly JourneyContract[], series: JourneySeries): boolean => {
  const ids = new Set(series.quotes.map((q) => q.id));
  return contracts.some((c) => ids.has(c.quote_id) && c.status !== "VOID" && c.status !== "TERMINATED");
};

interface Board {
  series: JourneySeries | null;
  // 가견적만 남은 판의 가견적 — null 이면 계열 기준(roughsFor).
  rough: JourneyQuote | null;
  // 이 시각 뒤 가견적만 본다(저장 단계가 이탈일 때만).
  since: string | null;
  restarted: boolean;
}

// 저장 단계가 이탈 — 가장 최근 활동 직전의 이탈 뒤 판만 본다. 그 이탈 뒤 살아 있는 견적이 있으면 재시작.
const lostStageBoard = (quotes: readonly JourneyQuote[], contracts: readonly JourneyContract[], all: readonly JourneySeries[], lastLostAt: string | null): Board => {
  const losses = lossTimesOf(quotes, contracts, lastLostAt);
  // 재시작 기준은 단계 이력 — 이미 이탈인 고객의 견적 이탈 정리는 이력을 안 남기므로 이력이 없을 때만 결정일·해지일로 대체.
  const lostAt = lastLostAt ?? losses[losses.length - 1] ?? null;
  const restarted = lostAt !== null && quotes.some((q) => q.status !== "LOST" && isAfter(quoteActivityAt(q), lostAt));
  const newest = latestOf(quotes.map(quoteActivityAt));
  const since = newest ? ([...losses].reverse().find((t) => isAfter(newest, t)) ?? null) : null;
  // 초안 계약은 붙잡지 않는다(수주 되돌린 계열이 재시작 판에 남지 않게) — SENT 이상은 고객에게 이미 나간 계약이라 보수적으로 계열을 남긴다.
  const sentContracts = contracts.filter((c) => c.status !== "DRAFT");
  const kept = since === null ? all : all.filter((s) => hasLiveContract(sentContracts, s) || s.quotes.some((q) => isAfter(quoteActivityAt(q), since)));
  return { series: latestSeries(kept), rough: null, since, restarted };
};

// 저장 단계가 이탈 아님 — (단계 수주면) 살아 있는 계약 계열 → 수주 계열, 그다음 가장 최근 계열, 그 계열이 이탈·해지면 더 최근의 살아 있는 계열·가견적.
const liveStageBoard = (quotes: readonly JourneyQuote[], contracts: readonly JourneyContract[], all: readonly JourneySeries[], won: boolean): Board => {
  const none = { rough: null, since: null, restarted: false };
  // 해지 계약만 남은 수주 계열은 끝난 판이다(파기만 남았으면 새 계약 대기라 살아 있다).
  const endedContract = (s: JourneySeries): boolean =>
    contracts.some((c) => c.status === "TERMINATED" && s.quotes.some((q) => q.id === c.quote_id)) && !hasLiveContract(contracts, s);
  const alive = all.filter((s) => s.latest.status !== "LOST" && !endedContract(s));
  const signedSeries = won
    ? (latestSeries(alive.filter((s) => hasLiveContract(contracts, s))) ?? latestSeries(alive.filter((s) => s.latest.status === "WON")))
    : null;
  if (signedSeries) return { series: signedSeries, ...none };
  const latest = latestSeries(all);
  if (!latest || (latest.latest.status !== "LOST" && !endedContract(latest))) return { series: latest, ...none };
  const liveSeries = latestSeries(alive);
  // 가견적은 수주가 없어 결론이 안 난 채 남는다 — 만든 시각과 상관없이 살아 있는 가견적이면 판 후보.
  const liveRough =
    quotes
      .filter((q) => q.kind === "ROUGH" && q.status !== "LOST")
      .sort(byQuoteActivity)
      .pop() ?? null;
  if (liveRough && (!liveSeries || byActivity(quoteActivityAt(liveRough), seriesActivityAt(liveSeries)) > 0))
    return { series: null, ...none, rough: liveRough };
  if (liveSeries) return { series: liveSeries, ...none };
  // 살아 있는 판이 없으면 — 끝난 계열 뒤에 손댄 가견적(전부 이탈)이 있으면 그 가견적 판.
  const lostAt = latest.latest.decided_at ?? latest.latest.created_at;
  const lateRough =
    quotes
      .filter((q) => q.kind === "ROUGH" && isAfter(quoteActivityAt(q), lostAt))
      .sort(byQuoteActivity)
      .pop() ?? null;
  return lateRough ? { series: null, ...none, rough: lateRough } : { series: latest, ...none };
};

// 여정의 뼈대 — 스텝퍼와 「다음 할 일」 이 같은 판정을 읽는다.
export const journeyContext = ({ customer, quotes, contracts, project, lastLostAt }: Omit<JourneyInput, "today">): JourneyContext => {
  const all = detailSeries(quotes);
  const contract = project ? (contracts.find((c) => c.id === project.contract_id) ?? null) : null;
  // 이탈 여부의 기준은 저장 단계 — 이탈 시각으로 판을 나누는 건 저장 단계가 이탈일 때만.
  const board: Board = project
    ? { series: all.find((s) => s.quotes.some((q) => q.id === contract?.quote_id)) ?? null, rough: null, since: null, restarted: false }
    : customer.stage === "LOST"
      ? lostStageBoard(quotes, contracts, all, lastLostAt)
      : liveStageBoard(quotes, contracts, all, customer.stage === "WON");
  const { series, since, restarted } = board;
  const live = project ? contract : contractOf(contracts, series);
  const roughs = roughsFor(quotes, all, series).filter((q) => since === null || isAfter(quoteActivityAt(q), since));
  const rough = board.rough ?? roughs[roughs.length - 1] ?? null;
  const signed = live !== null && SIGNED_CONTRACT_STATUSES.includes(live.status);

  let pos: number;
  if (project) pos = PROJECT_POSITION[project.status];
  else if (signed) pos = 4;
  else if (live || series?.latest.status === "WON") pos = 3;
  else if (series) pos = 2;
  else if (rough) pos = 1;
  else pos = 0;

  // 새 판이면 고객 단계가 아직 이탈이어도(견적 발송 전까지 그대로다) 이탈로 보지 않는다.
  const lost = project
    ? live?.status === "TERMINATED"
    : (customer.stage === "LOST" && !restarted) ||
      live?.status === "TERMINATED" ||
      (pos === 2 && series?.latest.status === "LOST") ||
      (pos === 1 && rough?.status === "LOST");

  return { series, live, rough, signed, pos, lost };
};

export const customerJourney = (input: JourneyInput): JourneyStep[] => {
  const { customer, project, today } = input;
  const { series, live, rough, signed, pos, lost } = journeyContext(input);

  const happened: Partial<Record<JourneyStepKey, boolean>> = {
    ROUGH: rough !== null,
    WARRANTY: project?.warranty_end_date != null,
  };

  const detailOf = (key: JourneyStepKey, state: JourneyStepState): Pick<JourneyStep, "when" | "doc"> => {
    if (state === "skipped") return { when: "건너뜀", doc: null };
    const now = state === "current" || state === "lost";
    switch (key) {
      case "INQUIRY":
        return { when: dayOf(customer.created_at), doc: null };
      case "ROUGH":
        if (!rough) return { when: optionalTodo(state), doc: null };
        return {
          when: rough.status === "LOST" ? QUOTE_STATUS_LABEL.LOST : dayOf(rough.sent_at ?? rough.created_at),
          doc: { label: rough.quote_no, href: quotePath(rough.id) },
        };
      case "DETAIL": {
        if (!series) return { when: null, doc: null };
        const q = series.latest;
        return {
          when: q.status === "WON" ? dayOf(q.decided_at) : QUOTE_STATUS_LABEL[q.status],
          doc: { label: `${q.quote_no} v${q.version}`, href: quotePath(q.id) },
        };
      }
      case "CONTRACT":
        if (!live) return { when: now ? "작성 전" : null, doc: null };
        return {
          when: signed && live.signed_at ? `${dayOf(live.signed_at)} 서명` : CONTRACT_STATUS_LABEL[live.status],
          doc: { label: live.contract_no, href: `/app/contracts/${live.id}` },
        };
      case "SCHEDULED":
        if (!project) return { when: now ? "현장 없음" : null, doc: null };
        if (state === "done") return { when: project.started_at ? `${dayOf(project.started_at)} 착공` : null, doc: null };
        if (now && project.planned_start_date)
          return { when: `착공 ${dDayLabel(dDayOf(project.planned_start_date, today))}`, doc: null };
        return { when: now ? "착공일 미정" : null, doc: null };
      case "IN_PROGRESS":
        if (!project) return { when: null, doc: null };
        if (state === "done") return { when: project.completion_date ? `${dayOf(project.completion_date)} 준공` : null, doc: null };
        if (now)
          return { when: elapsedText(project, today), doc: { label: "현장 보기", href: `/app/projects/${project.id}` } };
        return { when: null, doc: null };
      case "WARRANTY":
        if (project?.status === "WARRANTY" && project.warranty_end_date)
          return { when: `~ ${dayOf(project.warranty_end_date)}`, doc: null };
        return { when: state === "done" ? dayOf(project?.warranty_end_date) : optionalTodo(state), doc: null };
      case "CLOSED":
        if (project?.status === "CLOSED") return { when: dayOf(project.closed_at), doc: null };
        return { when: project?.planned_end_date ? `${dayOf(project.planned_end_date)} 예정` : null, doc: null };
    }
  };

  return JOURNEY_STEPS.map((key, i): JourneyStep => {
    const optional = OPTIONAL_JOURNEY_STEPS.includes(key);
    let state: JourneyStepState;
    if (pos === JOURNEY_STEPS.length - 1 && i === pos) state = lost ? "lost" : "done";
    else if (i < pos) state = optional && !happened[key] ? "skipped" : "done";
    else if (i === pos) state = lost ? "lost" : "current";
    else state = "todo";
    return { key, label: JOURNEY_STEP_LABEL[key], optional, state, ...detailOf(key, state) };
  });
};

const optionalTodo = (state: JourneyStepState): string | null => (state === "todo" ? "선택" : null);

// 시공중 경과 — 착공일부터 오늘까지(첫날 1일째) / 계획 기간.
const elapsedText = (project: JourneyProject, today: string): string | null => {
  const start = dayOf(project.started_at) ?? project.planned_start_date;
  if (!start) return null;
  const elapsed = Math.max(daysBetween(start, today) + 1, 1);
  if (!project.planned_start_date || !project.planned_end_date) return `${elapsed}일째`;
  return `${elapsed} / ${daysBetween(project.planned_start_date, project.planned_end_date) + 1}일`;
};

// 기본 현장 — 시공중 → 시공대기 → 유지보수 순으로 진행 중인 곳, 없으면 가장 최근에 만든 현장.
const ONGOING_ORDER: readonly ProjectStatus[] = ["IN_PROGRESS", "SCHEDULED", "WARRANTY"];

export const ongoingSite = <T extends Pick<Project, "id" | "status" | "created_at">>(projects: readonly T[]): T | null => {
  for (const status of ONGOING_ORDER) {
    const found = projects.filter((p) => p.status === status).sort(byCreated).pop();
    if (found) return found;
  }
  return null;
};

export const pickSite = <T extends Pick<Project, "id" | "status" | "created_at">>(
  projects: readonly T[],
  requested: string | null | undefined
): T | null =>
  projects.find((p) => p.id === requested) ?? ongoingSite(projects) ?? [...projects].sort(byCreated).pop() ?? null;

// ?site= 로 「새 영업」 을 고르는 값 — 현장 id(uuid)와 겹치지 않는다.
export const NEW_SALES_SITE = "new";

export interface NewSales<Q extends JourneyQuote, C extends JourneyContract> {
  quotes: Q[];
  contracts: C[];
}

// 새 영업 — 마지막 현장을 만든 뒤에 첫 버전을 만든 견적 계열(가견적 포함)과 그 계약. 현장이 없거나 그런 견적이 없으면 null.
export const newSalesOf = <Q extends JourneyQuote, C extends JourneyContract>(
  quotes: readonly Q[],
  contracts: readonly C[],
  projects: readonly Pick<Project, "created_at">[]
): NewSales<Q, C> | null => {
  const cutoff = projects.map((p) => p.created_at).sort().pop();
  if (!cutoff) return null;
  const starts = new Map<string, string>();
  for (const q of quotes) {
    const at = starts.get(q.quote_no);
    if (!at || q.created_at < at) starts.set(q.quote_no, q.created_at);
  }
  const fresh = quotes.filter((q) => (starts.get(q.quote_no) ?? q.created_at) > cutoff);
  if (fresh.length === 0) return null;
  const ids = new Set(fresh.map((q) => q.id));
  return { quotes: fresh, contracts: contracts.filter((c) => ids.has(c.quote_id)) };
};

export type JourneyPick<T> = { kind: "site"; project: T } | { kind: "new" } | { kind: "none" };

// 여정 기준 — 요청한 현장·새 영업 → 진행 중 현장 → 살아 있는 새 영업 → 가장 최근 현장.
export const pickJourney = <T extends Pick<Project, "id" | "status" | "created_at">>(
  projects: readonly T[],
  requested: string | null | undefined,
  newSales: { alive: boolean } | null
): JourneyPick<T> => {
  const asked = projects.find((p) => p.id === requested);
  if (asked) return { kind: "site", project: asked };
  if (requested === NEW_SALES_SITE && newSales) return { kind: "new" };
  const ongoing = ongoingSite(projects);
  if (ongoing) return { kind: "site", project: ongoing };
  if (newSales?.alive) return { kind: "new" };
  const last = [...projects].sort(byCreated).pop();
  return last ? { kind: "site", project: last } : { kind: "none" };
};

export interface JourneyPlace<Q extends JourneyQuote, C extends JourneyContract, T extends JourneyProject> {
  pick: JourneyPick<T>;
  fresh: NewSales<Q, C> | null;
  freshCtx: JourneyContext | null;
  ctx: JourneyContext;
  project: T | null;
}

// 여정 자리 — 새 영업 → pickJourney → journeyContext(requested 가 null 이면 고객 보드 열 배치와 같은 답).
export const journeyPlaceOf = <Q extends JourneyQuote, C extends JourneyContract, T extends JourneyProject>(
  customer: JourneyCustomer,
  quotes: readonly Q[],
  contracts: readonly C[],
  projects: readonly T[],
  lastLostAt: string | null,
  requested: string | null = null
): JourneyPlace<Q, C, T> => {
  const fresh = newSalesOf(quotes, contracts, projects);
  const freshCtx = fresh ? journeyContext({ customer, ...fresh, project: null, lastLostAt }) : null;
  const pick = pickJourney(projects, requested, freshCtx ? { alive: !freshCtx.lost } : null);
  const project = pick.kind === "site" ? pick.project : null;
  const ctx = pick.kind === "new" && freshCtx ? freshCtx : journeyContext({ customer, quotes, contracts, project, lastLostAt });
  return { pick, fresh, freshCtx, ctx, project };
};

// 새 영업 줄의 요약 — 지금 단계와 그 문서(상세견적 계열 최신 버전, 없으면 가견적).
export const newSalesSummaryOf = (ctx: Pick<JourneyContext, "series" | "rough" | "pos" | "lost">): { step: string; doc: string | null } => {
  const q = ctx.series?.latest ?? ctx.rough;
  return {
    step: `${JOURNEY_STEP_LABEL[JOURNEY_STEPS[ctx.pos]]}${ctx.lost ? " · 이탈" : ""}`,
    doc: q ? `${QUOTE_KIND_LABEL[q.kind]} ${q.quote_no} v${q.version} · ${QUOTE_STATUS_LABEL[q.status]}` : null,
  };
};

// 현장 줄의 공정 진행 — 완료 공정 비율과 한 줄 요약(착공 D-n · n / m일 · 준공일).
export const siteProgressOf = (
  project: JourneyProject,
  phases: readonly { status: ProjectPhaseStatus }[],
  today: string
): { pct: number; text: string } => {
  const done = phases.filter((p) => p.status === "DONE").length;
  const pct = project.status === "CLOSED" || project.status === "WARRANTY" ? 100 : phases.length ? Math.round((done / phases.length) * 100) : 0;
  switch (project.status) {
    case "SCHEDULED":
      return { pct, text: project.planned_start_date ? `착공 ${dDayLabel(dDayOf(project.planned_start_date, today))}` : "착공일 미정" };
    case "IN_PROGRESS":
      return { pct, text: [elapsedText(project, today), phases.length ? `공정 ${done}/${phases.length}` : null].filter(Boolean).join(" · ") };
    case "WARRANTY":
      return { pct, text: project.warranty_end_date ? `유지보수 ~ ${dayOf(project.warranty_end_date)}` : "유지보수" };
    case "CLOSED":
      return { pct, text: project.completion_date ? `${dayOf(project.completion_date)} 준공` : "완료" };
  }
};

// 현장 줄의 기간 — 착공 → 준공(실제가 있으면 실제, 없으면 계획).
export const sitePeriodOf = (project: JourneyProject): string | null => {
  const start = dayOf(project.started_at) ?? project.planned_start_date;
  const end = project.completion_date ?? project.planned_end_date;
  if (!start && !end) return null;
  return `${start ?? "미정"} → ${end ?? "미정"}`;
};
